import { PageLayout } from "@components/ui/PageLayout"
import {
  routeLocaleFromPathname,
  routePageClass,
  routePageFromPathname,
  useRoutePagePathname,
} from "@components/ui/route-page-context"
import { publicAssetBaseUrl } from "@lib/meta"
import * as m from "@paraglide/messages"
import { dashboardAppearanceClass } from "@playbrain/config/dashboardAppearance"
import { readDashboardAppearance, resolveAppearance } from "@playbrain/config/dashboardAppearanceRuntime"
import type { SupportedLocale } from "@site-types/content"
import type { QueryClient } from "@tanstack/react-query"
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  useRouterState,
} from "@tanstack/react-router"
import { faviconFor } from "@utils/favicon"
import type { ReactNode } from "react"
import appCss from "../global.css?url"
import { analyticsScript, remoteImageSkeletonScript, viewTransitionRouterScript } from "../lib/root-scripts"
import { ErrorPage } from "./-ErrorPage"

const fontAwesomeKitUrl = "https://kit.fontawesome.com/70d7c74973.css"
const anonymousCrossOrigin = "anonymous" as const

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient
}>()({
  head: () => ({
    title: "PLAYBRAIN Inc.",
    meta: [
      { title: "PLAYBRAIN Inc." },
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "stylesheet", href: fontAwesomeKitUrl },
      { rel: "sitemap", href: "/sitemap.xml" },
      { rel: "preconnect", href: "https://use.typekit.net", crossOrigin: anonymousCrossOrigin },
      { rel: "preconnect", href: "https://p.typekit.net", crossOrigin: anonymousCrossOrigin },
      { rel: "preconnect", href: publicAssetBaseUrl(), crossOrigin: anonymousCrossOrigin },
    ],
  }),
  component: RootComponent,
  errorComponent: RootError,
  notFoundComponent: NotFound,
})

function RootComponent() {
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  return (
    <RootDocument pageClass={pageClassFromPathname(pathname)}>
      <Outlet />
    </RootDocument>
  )
}

function pageClassFromPathname(pathname: string) {
  if (isDashboardPagePath(pathname)) return "dashboard"

  const page = routePageFromPathname(pathname)

  return routePageClass(page)
}

function isDashboardPagePath(pathname: string) {
  const normalizedPathname = pathname.split("?")[0]?.split("#")[0] ?? pathname

  return (
    normalizedPathname === "/login" ||
    normalizedPathname === "/dashboard" ||
    normalizedPathname.startsWith("/dashboard/")
  )
}

function RootDocument({ children, pageClass }: Readonly<{ children: ReactNode; pageClass: string }>) {
  const pathname = useRoutePagePathname()
  const locale = routeLocaleFromPathname(pathname)

  /*
   * The dashboard's appearance is decided here, in the markup, rather than by
   * a script or an effect. Radix publishes its dark scales under
   * `:is(.dark, .dark-theme) …`, so this one class settles every `<Theme>`
   * below it — and because it is server-rendered, the first byte of HTML is
   * already the right theme. Nothing re-renders to change it and nothing
   * flashes.
   *
   * Only the dashboard has an appearance. The public site is its own design
   * and does not read these tokens.
   *
   * React owns this attribute and re-renders the root on every navigation, so
   * `resolveAppearance` has to be the same function the provider uses — see
   * its comment. It is; that is what stops the two from fighting over the
   * class after the provider corrects a "system" user.
   */
  const isDashboard = pageClass === "dashboard"
  const appearanceClass = isDashboard
    ? dashboardAppearanceClass(resolveAppearance(readDashboardAppearance()))
    : undefined

  return (
    <html
      lang={locale}
      className={
        [pageClass === "index" || isDashboard ? pageClass : undefined, appearanceClass]
          .filter(Boolean)
          .join(" ") || undefined
      }
    >
      <head>
        {/*
         * No <title> here. One used to sit above <HeadContent /> and won every
         * time, so every page in the site — PLAYBRAIN and PLAYDATA alike —
         * showed "PLAYBRAIN Inc." in the tab no matter what its route head
         * resolved. The root route still carries that title as the default,
         * which is where a fallback belongs: a route can now override it.
         */}
        <link
          rel="icon"
          {...faviconFor(pageClass, pathname)}
        />
        <HeadContent />
        <script dangerouslySetInnerHTML={{ __html: analyticsScript }} />
        <script dangerouslySetInnerHTML={{ __html: remoteImageSkeletonScript }} />
        <script dangerouslySetInnerHTML={{ __html: viewTransitionRouterScript }} />
      </head>
      <body className={pageClass}>
        {children}
        <Scripts />
      </body>
    </html>
  )
}

function NotFound() {
  return (
    <PageLayout page="error404">
      <ErrorPage />
    </PageLayout>
  )
}

function RootError() {
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const locale = routeLocaleFromPathname(pathname) as SupportedLocale

  return (
    <PageLayout
      page={m.error_500_meta_page({}, { locale })}
      title={m.error_500_meta_pageTitle({}, { locale })}
    >
      <title>{m.error_500_meta_title({}, { locale })}</title>
      <meta
        name="robots"
        content="noindex,nofollow"
      />
      <div
        className="mx-auto min-h-[calc(100lvh-51px)] max-w-[600px] pt-[5%] text-center"
        data-testid="page-error"
      >
        <h1 className="text-[34px]">{m.error_500_label({}, { locale })}</h1>
        <div>
          <p>{m.error_500_description({}, { locale })}</p>
        </div>
        <a href={`/${locale}`}>{m.back_toHome({}, { locale })}</a>
      </div>
    </PageLayout>
  )
}
